import React, { useLayoutEffect, useRef } from 'react';
import { Box, Presence } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { EASE_OUT } from '@/components/ui/motion';

interface OutputBadgeTransitionProps {
	pending: boolean;
	ready: boolean;
	pendingContent: React.ReactNode;
	children: React.ReactNode;
}

const outputEnter = keyframes`
	from { opacity: 0; transform: scale(0.98); }
	to { opacity: 1; transform: scale(1); }
`;

const outputExit = keyframes`
	from { opacity: 1; transform: scale(1); }
	to { opacity: 0; transform: scale(0.98); }
`;

const fadeEnter = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`;

const fadeExit = keyframes`
	from { opacity: 1; }
	to { opacity: 0; }
`;

const presenceCss = {
	'&[data-state=open]': { animation: `${outputEnter} 180ms ${EASE_OUT} both` },
	'&[data-state=closed]': { animation: `${outputExit} 140ms ${EASE_OUT} both` },
	'@media (prefers-reduced-motion: reduce)': {
		'&[data-state=open]': { animation: `${fadeEnter} 120ms ${EASE_OUT} both` },
		'&[data-state=closed]': { animation: `${fadeExit} 100ms ${EASE_OUT} both` },
	},
} as const;

/** Crossfades a generated-output placeholder into its completed badge in one shared footprint. */
const OutputBadgeTransition: React.FC<OutputBadgeTransitionProps> = (props) => {
	const { pending, ready, pendingContent, children } = props;
	const pendingVisible = pending && !ready;
	const lastPendingContent = useRef(pendingContent);
	useLayoutEffect(() => {
		if (pendingVisible) lastPendingContent.current = pendingContent;
	}, [pendingContent, pendingVisible]);
	const renderedPendingContent = pendingVisible ? pendingContent : lastPendingContent.current;

	return (
		<Box display="inline-grid" minW={0} maxW="full" alignItems="center">
			<Presence
				present={pendingVisible}
				unmountOnExit
				skipAnimationOnMount
				gridArea="1 / 1"
				minW={0}
				transformOrigin="left center"
				css={presenceCss}
			>
				{renderedPendingContent}
			</Presence>
			<Presence
				present={ready}
				lazyMount
				unmountOnExit
				skipAnimationOnMount
				gridArea="1 / 1"
				minW={0}
				transformOrigin="left center"
				css={presenceCss}
			>
				{children}
			</Presence>
		</Box>
	);
};

export default OutputBadgeTransition;
